import _ from "lodash";
import React from "react";

import { graphql, compose } from "lib/apolloClient";
import Loading from "UIComponents/Loading";
import withLoader from "UIComponents/GqlWithLoader";
import { getDepartmentUsersQuery } from "MessagingV2/modules/MessagingQuery";
import { connect } from "react-redux";

import {
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import MemberCard from "MessagingV2/components/Channels/OverviewAndSettingsModal/components/MemberCard";

import useI18n from "Hooks/UseI18n";
import prepareLoadMoreFeed from "Utils/PrepareLoadMoreFeedHOF";
import { EmptyState, Modal } from "@toddle-edu/ds-web";
import { NoTeachersIllustration } from "@toddle-edu/ds-theme";
import { useHandleScroll } from "Utils/CustomHooks";

import classes from "./MembersModal.scss";
import {
  getOrganizationId,
  getDefaultNameSortPreference,
} from "modules/CommonSelectors";

const styles = {
  modalBodyStyles: {
    padding: "0px",
    overflow: "hidden",
  },
};

const DepartmentDetailContainer = ({
  users,
  networkStatus,
  loadMoreFeed,
  hasNextPage,
  defaultStudentNameSortPreference,
  isLoadingMore,
}) => {
  const { t } = useI18n(["common"]);

  const containerRef = React.useRef(null);

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  if (_.isEmpty(users)) {
    return (
      <EmptyState
        dsVersion="2.0"
        illustration={NoTeachersIllustration}
        title={t("common:no_member_in_department")}
      />
    );
  }

  return (
    <div className={classes.root} ref={containerRef}>
      {_.map(users, ({ node: user }) => {
        const id = _.get(user, "id");
        const profileImage = _.get(user, "profileImage");
        const name = getFormattedNameForUserType({
          user,
          orderBy: defaultStudentNameSortPreference,
        });

        return (
          <MemberCard
            key={id}
            avatarId={id}
            mainText={name}
            subText={getSubText({
              messagedUserObj: user,
              messagedUserRole: _.get(user, "type"),
              t,
              defaultStudentNameSortPreference,
            })}
            profileImage={profileImage}
          />
        );
      })}
      {isLoadingMore ? (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    defaultStudentNameSortPreference: getDefaultNameSortPreference(state),
  };
};

const ModalBodyWithLoader = compose(
  connect(mapStateToProps),
  graphql(getDepartmentUsersQuery, {
    name: "getDepartmentUsers",
    skip: ({ id }) => !id,
    options: ({ id }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id,
        organizationId: getOrganizationId(),
        input: {
          first: 20,
        },
        portalType: "PLANNER",
      },
    }),
    props({ getDepartmentUsers }) {
      const node = _.get(getDepartmentUsers, "node", {});

      const networkStatus = _.get(getDepartmentUsers, "networkStatus");
      const queryVariables = _.get(getDepartmentUsers, "variables", {});
      const pageInfo = _.get(node, "users.pageInfo", {});
      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const isLoadingMore = networkStatus === 3;
      const users = _.get(node, "users.edges", []);

      return {
        isLoading: _.includes([1, 2], networkStatus),
        isData: !_.isEmpty(node),
        users,
        hasNextPage,
        networkStatus,
        isLoadingMore,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...queryVariables,
            input: {
              ...queryVariables.input,
              before: _.get(pageInfo, "endCursor", undefined),
            },
          },
          queryData: getDepartmentUsers,
          key: "users",
        }),
      };
    },
  }),
  withLoader
)(DepartmentDetailContainer);

const MembersModal = ({ department, onClose }) => {
  const { t } = useI18n(["common"]);
  const id = _.get(department, "id");
  const name = _.get(department, "name");
  return (
    <Modal width="480px" height="560px" onClose={onClose} dsVersion="2.0">
      <Modal.Header
        title={t("common:members_in_label", {
          label: _.truncate(name, { length: 60 }),
        })}
        hasSeparator
      />

      <Modal.Body style={styles.modalBodyStyles}>
        <ModalBodyWithLoader id={id} />
      </Modal.Body>
    </Modal>
  );
};

export default MembersModal;
